import { t } from "@lingui/macro";
import {
  CaretDownIcon,
  ChatTeardropTextIcon,
  CircleNotchIcon,
  ExamIcon,
  MagicWandIcon,
  PenNibIcon,
} from "@phosphor-icons/react";
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { useState } from "react";

import { toast } from "../hooks/use-toast";
import { isAiInsufficientPointsError } from "../services/ai/errors";
import { changeTone } from "../services/openai/change-tone";
import { fixGrammar } from "../services/openai/fix-grammar";
import { improveWriting } from "../services/openai/improve-writing";
import { useDialog } from "../stores/dialog";

type Action = "improve" | "fix" | "tone";
type Mood = "casual" | "professional" | "confident" | "friendly";

type Props = {
  value: string;
  onChange: (value: string) => void;
  className?: string;
};

export const AiActions = ({ value, onChange, className }: Props) => {
  const { open: openAiPacks } = useDialog("ai-packs");
  const [loading, setLoading] = useState<Action | false>(false);
  const [preview, setPreview] = useState<{ action: Action; text: string } | null>(null);

  const onClick = async (action: Action, mood?: Mood) => {
    try {
      setLoading(action);
      setPreview(null);

      let result = value;

      if (action === "improve") result = await improveWriting(value);
      if (action === "fix") result = await fixGrammar(value);
      if (action === "tone" && mood) result = await changeTone(value, mood);

      setPreview({ action, text: result });
    } catch (error) {
      if (isAiInsufficientPointsError(error)) {
        openAiPacks("create", { id: "ai-packs", item: { type: "improve" } });
        return;
      }

      toast({
        variant: "error",
        title: t`Oops, the server returned an error.`,
        description: (error as Error).message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={cn(
        "bg-secondary-accent/50 relative mt-4 rounded p-3",
        "flex flex-wrap items-center justify-center gap-3",
        className,
      )}
    >
      {/* <div className="absolute -left-5 z-10 rounded-lg border-focus-orange">
        <Badge
          outline
          variant="primary"
          className="-rotate-90 bg-background px-2 text-[10px] leading-[10px]"
        >
          <MagicWandIcon size={10} className="mr-1" />
          {t`AI`}
        </Badge>
      </div> */}
      <Button
        size="sm"
        variant="outline"
        disabled={!!loading}
        onClick={() => onClick("improve")}
        className="rounded-lg bg-focus/20 text-text"
      >
        {loading === "improve" ? <CircleNotchIcon className="animate-spin" /> : <PenNibIcon />}
        <span className="ml-2 text-xs">Amélioration de votre texte</span>
      </Button>
      <Button
        size="sm"
        variant="outline"
        disabled={!!loading}
        onClick={() => onClick("fix")}
        className="rounded-lg bg-focus-orange/30 text-focus-orange hover:bg-focus-orange/50 hover:text-focus-orange"
      >
        {loading === "fix" ? <CircleNotchIcon className="animate-spin" /> : <ExamIcon />}
        <span className="ml-2 text-xs">Verification Grammaticale</span>
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            variant="outline"
            disabled={!!loading}
            className="rounded-lg bg-focus/20 text-focus-orange"
          >
            {loading === "tone" ? (
              <CircleNotchIcon className="animate-spin" />
            ) : (
              <ChatTeardropTextIcon className="text-text" />
            )}
            <span className="mx-2 text-xs text-text">Ajouter l'émotion approprié </span>
            <CaretDownIcon className="text-text" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuItem onClick={() => onClick("tone", "casual")}>
            <span role="img" aria-label={t`Casual`}>
              🙂
            </span>
            <span className="ml-2">{t`Casual`}</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onClick("tone", "professional")}>
            <span role="img" aria-label={t`Professional`}>
              💼
            </span>
            <span className="ml-2">{t`Professional`}</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onClick("tone", "confident")}>
            <span role="img" aria-label={t`Confident`}>
              😎
            </span>
            <span className="ml-2">{t`Confident`}</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onClick("tone", "friendly")}>
            <span role="img" aria-label={t`Friendly`}>
              😊
            </span>
            <span className="ml-2">{t`Friendly`}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>{" "}
      {preview ? (
        <div className="absolute right-0 top-0 z-[999] w-full max-w-xl transform-gpu rounded-3xl border border-accent-primary bg-surface p-4 shadow-2xl">
          <div className="flex items-center justify-between gap-3">
            <div>
              {/* <p className="text-xs font-semibold tracking-[0.1em] text-text-muted">{t`Confirmer cette suggestion`}</p> */}
              <p className="text-sm font-semibold text-text">
                Confirmer cette suggestion{" "}
                {preview.action === "improve"
                  ? t`'d'amélioration`
                  : preview.action === "fix"
                    ? t`de correction`
                    : t`de ton`}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="ghost"
                className="rounded-lg bg-focus-rose text-white hover:bg-focus-rose hover:text-white"
                onClick={() => setPreview(null)}
              >
                {t`Annuler`}
              </Button>
              <Button
                size="sm"
                variant="primary"
                className="rounded-lg"
                onClick={() => {
                  onChange(preview.text);
                  setPreview(null);
                }}
              >
                {t`Appliquer`}
              </Button>
            </div>
          </div>
          <div className="mt-3 max-h-52 overflow-auto rounded-2xl border border-border-subtle bg-surface p-3 text-sm leading-relaxed text-text">
            {preview.text}
          </div>
        </div>
      ) : null}{" "}
    </div>
  );
};
